import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { renderAsync } from "docx-preview";
import { loadStripe } from "@stripe/stripe-js";
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { SWAL_SETTINGS, ROLE } from "../../../../utils/Constants";
import { handleServerValidations, setLocalKey, getLocalKey, removeLocalKey, getUser, logOutUser, getCustomerPortalUrl } from "../../../../utils/commonfunction";
import clubBillingService from "../../services/club-billing.service";
import * as websiteService from "../../../website/services/website.services";
import * as globalsettingService from "../../services/globalsetting.services";
import NativeDocxViewer from "../cms/agreements/NativeDocxViewer";
import { useQuery, useMutation } from "react-query";
import { useTranslation } from "react-i18next";
import moment from "moment/moment";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

/**
 * Helper to determine if a canvas has actual drawn strokes or is empty/blank
 */
const isCanvasEmpty = (canvas) => {
    if (!canvas) return true;
    const ctx = canvas.getContext("2d");
    if (!ctx) return true;
    try {
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const data = imgData.data;
        let strokePixelCount = 0;
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];
            // If pixel has opacity and is not white (i.e. dark ink stroke)
            if (a > 30 && (r < 220 || g < 220 || b < 220)) {
                strokePixelCount++;
                if (strokePixelCount >= 25) {
                    return false;
                }
            }
        }
        return true;
    } catch (e) {
        return false;
    }
};

/**
 * Interactive Canvas Signature Drawing Pad
 */
const CanvasSignaturePad = ({ onCanvasRef, setHasSignature, hasError, onStroke }) => {
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    useEffect(() => {
        if (canvasRef.current) {
            if (onCanvasRef) onCanvasRef(canvasRef.current);
            const canvas = canvasRef.current;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
    }, [onCanvasRef]);

    const getCoordinates = (e) => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };
        const rect = canvas.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const clientY = e.touches ? e.touches[0].clientY : e.clientY;
        return {
            x: (clientX - rect.left) * (canvas.width / rect.width),
            y: (clientY - rect.top) * (canvas.height / rect.height)
        };
    };

    const startDrawing = (e) => {
        e.preventDefault();
        const { x, y } = getCoordinates(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
    };

    const draw = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const { x, y } = getCoordinates(e);
        const ctx = canvasRef.current.getContext("2d");
        ctx.lineTo(x, y);
        ctx.strokeStyle = "#000000";
        ctx.lineWidth = 2.5;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.stroke();
        if (onStroke) onStroke();
    };

    const stopDrawing = () => {
        if (!isDrawing) return;
        setIsDrawing(false);
        const empty = isCanvasEmpty(canvasRef.current);
        setHasSignature(!empty);
        if (!empty && onStroke) {
            onStroke();
        }
    };

    return (
        <canvas
            ref={canvasRef}
            width={750}
            height={160}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
            style={{
                width: "100%",
                height: 160,
                border: hasError ? "2px dashed #dc3545" : "2px dashed #007d7f",
                borderRadius: 8,
                backgroundColor: hasError ? "#fff8f8" : "#ffffff",
                cursor: "crosshair",
                touchAction: "none",
                transition: "border-color 0.2s, background-color 0.2s"
            }}
        />
    );
};

const ClubBillingDashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    // Extract & store token synchronously before any query fires
    (() => {
        const params = new URLSearchParams(location.search);
        const token = params.get("token");
        if (token) {
            const clean = token.trim();
            removeLocalKey(ROLE.SUPER_ADMIN);
            removeLocalKey(ROLE.SUB_ADMIN);
            removeLocalKey(ROLE.PLAYER);
            removeLocalKey(ROLE.PARENT);
            removeLocalKey(ROLE.NON_AFFILIATED_USER);
            setLocalKey(ROLE.CLUB_ADMIN, JSON.stringify({ token: clean }));
            // Sanitize URL immediately to prevent token exposure in history, logs, screenshots, and Referer headers
            params.delete("token");
            const newSearch = params.toString() ? `?${params.toString()}` : "";
            try {
                window.history.replaceState({}, document.title, `${window.location.pathname}${newSearch}`);
            } catch (e) {}
        }
    })();

    // 1: Commercial Agreement, 2: Stripe Mandate Setup, 3: Complete Dashboard
    const [currentStep, setCurrentStep] = useState(1);
    const [clientSecret, setClientSecret] = useState(null);

    // Global settings state
    const [isSettingsLoaded, setIsSettingsLoaded] = useState(false);

    // CMS Normal Agreement State
    const [cmsAgreement, setCmsAgreement] = useState({ title: "Customer Agreement", description: "", loading: true });
    const [authName, setAuthName] = useState("");
    const [authTitle, setAuthTitle] = useState("");
    const [fullCompanyName, setFullCompanyName] = useState("");
    const [companyNumber, setCompanyNumber] = useState("");
    const [isRatified, setIsRatified] = useState(false);
    const [hasSignature, setHasSignature] = useState(false);
    const [signatureError, setSignatureError] = useState("");
    const canvasInstanceRef = useRef(null);

    const handleCanvasRef = useCallback((ref) => {
        canvasInstanceRef.current = ref;
    }, []);

    // Avatar & Logout Popup state
    const [showLogoutPopup, setShowLogoutPopup] = useState(false);
    const popupRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setShowLogoutPopup(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleLogout = () => {
        setShowLogoutPopup(false);
        Swal.fire({
            customClass: "swal-wide",
            title: t("msg_are_you_sure", "Are you sure?"),
            text: t("text_you_want_to_logout", "You want to logout"),
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#007d7f",
            cancelButtonColor: "#d33",
            confirmButtonText: t("btn_yes", "Yes, Logout"),
            cancelButtonText: t("btn_cancel", "Cancel")
        }).then((result) => {
            if (result.isConfirmed) {
                logOutUser(ROLE.SUPER_ADMIN);
                removeLocalKey(ROLE.CLUB_ADMIN);
                removeLocalKey("is_customer_portal");
                const portalUrl = process.env.REACT_APP_CUSTOMER_PORTAL_URL || getCustomerPortalUrl() || "http://portal.localhost:3000";
                window.location.href = portalUrl;
            }
        });
    };

    const [agreementType, setAgreementType] = useState("founder-agreement");

    const fetchCmsForType = async (type) => {
        const isPartner = type === "Partner" || type === "Founder";
        const primarySlug = isPartner ? "founder-agreement" : "standard-agreement";
        const fallbackSlugs = isPartner ? ["partner-agreement", "customer_agreement"] : ["customer_agreement"];
        const slugsToTry = [primarySlug, ...fallbackSlugs];

        for (const slug of slugsToTry) {
            try {
                const res = await websiteService.CMSPage(slug);
                const data = res?.data || res;
                if (data && (data.description || data.long_description)) {
                    setCmsAgreement({
                        title: data.title || data.page_title || (isPartner ? "Founder Agreement" : "Standard Agreement"),
                        description: data.description || data.long_description,
                        loading: false
                    });
                    return;
                }
            } catch (err) {
                console.warn(`CMS page for ${slug} fetch attempt failed, checking next...`);
            }
        }
        setCmsAgreement(prev => ({ ...prev, loading: false }));
    };

    useEffect(() => {
        globalsettingService.Details().then(res => {
            const data = res?.data || res;
            if (data) {
                fetchCmsForType(agreementType);
            }
            setIsSettingsLoaded(true);
        }).catch(err => {
            console.error("Failed to load global settings", err);
            setIsSettingsLoaded(true);
        });
    }, []);

    /* ── API Mutations ── */
    const setupCheckoutMutation = useMutation(
        () => clubBillingService.setupCheckout(),
        {
            onSuccess: (res) => {
                if (res.success && res.data?.client_secret) {
                    setClientSecret(res.data.client_secret);
                    setCurrentStep(2);
                } else {
                    Swal.fire({ icon: "error", text: handleServerValidations(res), ...SWAL_SETTINGS });
                }
            },
            onError: (err) => Swal.fire({ icon: "error", text: handleServerValidations(err), ...SWAL_SETTINGS }),
        }
    );



    // Normal CMS Agreement Signature Mutation
    const signNormalAgreementMutation = useMutation(
        (data) => clubBillingService.signAgreement(data),
        {
            onSuccess: (res) => {
                if (res.success) {
                    Swal.fire({
                        icon: "success",
                        title: "Commercial Agreement Signed!",
                        text: "Your digital signature has been accepted and saved.",
                        timer: 2000,
                        showConfirmButton: false,
                        ...SWAL_SETTINGS
                    });
                    setupCheckoutMutation.mutate();
                } else {
                    Swal.fire({ icon: "error", text: handleServerValidations(res), ...SWAL_SETTINGS });
                }
            },
            onError: (err) => Swal.fire({ icon: "error", text: handleServerValidations(err), ...SWAL_SETTINGS }),
        }
    );

    const verifySetupMutation = useMutation(
        (sessionId) => clubBillingService.verifySetup(sessionId),
        {
            onSuccess: (res) => {
                if (res.success) {
                    refetchStatus();
                } else {
                    Swal.fire({ icon: "error", text: handleServerValidations(res), ...SWAL_SETTINGS });
                }
            },
            onError: (err) => Swal.fire({ icon: "error", text: handleServerValidations(err), ...SWAL_SETTINGS }),
        }
    );

    const searchParams = new URLSearchParams(location.search);
    const sessionIdInUrl = searchParams.get("session_id");

    // Fetch Billing Status
    const { data: statusData, refetch: refetchStatus, isLoading: isStatusLoading } = useQuery(
        "billing_status",
        () => clubBillingService.getStatus().then(res => res.success ? res.data : null),
        {
            onSuccess: (data) => {
                if (data) {
                    const resolvedType = data.clubType || "Founder";
                    setAgreementType(resolvedType);
                    if (data.activeAgreement) {
                        setCmsAgreement({
                            title: data.activeAgreement.title || "Founder Agreement",
                            description: data.activeAgreement.content || "",
                            loading: false
                        });
                    } else {
                        fetchCmsForType(resolvedType);
                    }
                    if (data.firstName || data.lastName) {
                        setAuthName(`${data.firstName || ''} ${data.lastName || ''}`.trim());
                    }
                    if (data.title || data.designation || data.clubDetails?.title) {
                        setAuthTitle(data.title || data.designation || data.clubDetails?.title);
                    }
                    if (data.fullCompanyName || data.companyName || data.clubDetails?.name || data.clubName) {
                        setFullCompanyName(data.fullCompanyName || data.companyName || data.clubDetails?.name || data.clubName || "");
                    }
                    if (data.companyNumber || data.clubDetails?.companyNumber) {
                        setCompanyNumber(data.companyNumber || data.clubDetails?.companyNumber || "");
                    }
                    if (data.commercialAgreementSigned) {

                        // Check if mandate setup is actually complete
                        const isMandateSetupComplete =
                            data.billingStatus === "active" ||
                            data.billingStatus === "billed" ||
                            data.billingStatus === "free_tier" ||
                            data.billingStatus === "billable" ||
                            data.billingStatus === "past_due" ||
                            !!data.defaultPaymentMethodId ||
                            !!data.paymentMethod;

                        if (isMandateSetupComplete) {
                            setCurrentStep(3);
                        } else if (!clientSecret) {
                            setupCheckoutMutation.mutate();
                        }
                    } else {
                        setCurrentStep(1);
                    }
                }
            },
            onError: (err) => console.error("Failed to fetch billing status", err)
        }
    );

    const portalSessionMutation = useMutation(
        () => clubBillingService.createPortalSession(),
        {
            onSuccess: (res) => {
                if (res.success && res.data?.url) {
                    window.location.href = res.data.url;
                } else {
                    Swal.fire({ icon: "error", text: handleServerValidations(res), ...SWAL_SETTINGS });
                }
            },
            onError: (err) => Swal.fire({ icon: "error", text: handleServerValidations(err), ...SWAL_SETTINGS }),
        }
    );

    // Synchronously verify session_id if returning from Stripe Setup Checkout
    useEffect(() => {
        if (sessionIdInUrl && !verifySetupMutation.isLoading && !verifySetupMutation.isSuccess && !verifySetupMutation.isError) {
            verifySetupMutation.mutate(sessionIdInUrl);
        }
    }, [sessionIdInUrl]);



    const handleClearSignature = () => {
        if (canvasInstanceRef.current) {
            const canvas = canvasInstanceRef.current;
            const ctx = canvas.getContext("2d");
            ctx.fillStyle = "#ffffff";
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            setHasSignature(false);
            setSignatureError("");
        }
    };

    const handleSignNormalAgreement = () => {
        setSignatureError("");

        if (!authName || !authName.trim()) {
            return Swal.fire({ icon: "warning", text: "Please enter Representative Name.", ...SWAL_SETTINGS });
        }
        if (!authTitle || !authTitle.trim()) {
            return Swal.fire({ icon: "warning", text: "Please enter Official Title.", ...SWAL_SETTINGS });
        }

        const canvas = canvasInstanceRef.current;
        const canvasEmpty = isCanvasEmpty(canvas);

        if (!hasSignature || canvasEmpty) {
            setSignatureError("Digital signature is required. Please draw your signature in the box above.");
            return Swal.fire({ 
                icon: "warning", 
                title: "Signature Required",
                text: "Please draw your digital signature before continuing.", 
                ...SWAL_SETTINGS 
            });
        }
        if (!isRatified) {
            return Swal.fire({ icon: "warning", text: "You must tick the ratification checkbox to confirm authorisation.", ...SWAL_SETTINGS });
        }

        const signatureBase64 = canvas ? canvas.toDataURL("image/png") : "";

        signNormalAgreementMutation.mutate({
            authName: authName.trim(),
            authTitle: authTitle.trim(),
            fullCompanyName: fullCompanyName.trim(),
            companyNumber: companyNumber.trim(),
            signatureDataUrl: signatureBase64,
            signatureBase64,
            agreementType: cmsAgreement.title || "Customer Agreement"
        });
    };

    const handleDownloadAgreement = async () => {
        let tempContainer = null;
        try {
            Swal.fire({
                title: "Generating PDF...",
                text: "Converting agreement document to PDF...",
                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            const apiBase = process.env.REACT_APP_API_URL || "http://localhost:7000";
            let cleanBase = apiBase.endsWith("/") ? apiBase.slice(0, -1) : apiBase;
            if (!cleanBase.includes("/webservice/api/v1")) {
                cleanBase = `${cleanBase}/webservice/api/v1`;
            }

            let token = "";
            try {
                const rolesToTry = ["super_admin", "sub_admin", "club_admin"];
                for (const r of rolesToTry) {
                    const uData = getUser(r);
                    if (uData && uData.token) { token = uData.token; break; }
                    const raw = getLocalKey(r) || localStorage.getItem(r);
                    if (raw) {
                        try {
                            const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                            if (parsed && parsed.token) { token = parsed.token; break; }
                        } catch (e) { }
                    }
                }
            } catch (e) { }

            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            // 1. Fetch DOCX blob (from S3 signed URL or render-docx API endpoint)
            let docxBlob = null;
            try {
                const s3Res = await fetch(`${cleanBase}/club-admin/billing/agreement?json=true&format=docx`, { headers });
                const s3Data = await s3Res.json();
                const docxUrl = s3Data?.data?.docxDownloadUrl || s3Data?.data?.downloadUrl;
                if (docxUrl) {
                    const docxRes = await fetch(docxUrl);
                    if (docxRes.ok) docxBlob = await docxRes.blob();
                }
            } catch (e) { }

            if (!docxBlob) {
                const downloadUrl = `${cleanBase}/club-admin/billing/render-docx`;
                const res = await fetch(downloadUrl, { headers });
                if (!res.ok) throw new Error(`Failed to fetch document (${res.status})`);
                docxBlob = await res.blob();
            }

            // 2. Render DOCX into offscreen DOM element using docx-preview
            tempContainer = document.createElement("div");
            tempContainer.style.position = "fixed";
            tempContainer.style.left = "-9999px";
            tempContainer.style.top = "0px";
            tempContainer.style.width = "850px";
            tempContainer.style.background = "#ffffff";
            document.body.appendChild(tempContainer);

            await renderAsync(docxBlob, tempContainer, null, {
                className: "docx-container",
                inWrapper: true,
                ignoreWidth: false,
                ignoreHeight: false,
                experimental: true,
                useBase64URL: true
            });

            // Small delay for DOM layout & signatures/fonts to render
            await new Promise(r => setTimeout(r, 600));

            // 3. Convert each page section or sliced canvas into individual PDF pages
            const pdf = new jsPDF("p", "pt", "a4");
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();

            const pageSections = tempContainer.querySelectorAll("section.docx");

            if (pageSections && pageSections.length > 0) {
                // Multi-page DOCX renderer: Render each DOCX page section to its own PDF page
                for (let i = 0; i < pageSections.length; i++) {
                    const sec = pageSections[i];
                    const canvas = await html2canvas(sec, {
                        scale: 2,
                        useCORS: true,
                        logging: false,
                        backgroundColor: "#ffffff"
                    });
                    const imgData = canvas.toDataURL("image/jpeg", 0.95);
                    if (i > 0) pdf.addPage();
                    pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);
                }
            } else {
                // Canvas slice fallback for continuous containers
                const canvas = await html2canvas(tempContainer, {
                    scale: 2,
                    useCORS: true,
                    logging: false,
                    backgroundColor: "#ffffff"
                });

                const imgWidth = canvas.width;
                const imgHeight = canvas.height;
                const pageCanvasHeight = Math.floor((imgWidth * pdfHeight) / pdfWidth);

                let heightLeft = imgHeight;
                let position = 0;

                const pageCanvas = document.createElement("canvas");
                const ctx = pageCanvas.getContext("2d");
                pageCanvas.width = imgWidth;

                let pageIdx = 0;
                while (heightLeft > 0) {
                    const chunkHeight = Math.min(heightLeft, pageCanvasHeight);
                    pageCanvas.height = pageCanvasHeight;

                    ctx.fillStyle = "#ffffff";
                    ctx.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
                    ctx.drawImage(canvas, 0, position, imgWidth, chunkHeight, 0, 0, imgWidth, chunkHeight);

                    const imgData = pageCanvas.toDataURL("image/jpeg", 0.95);

                    if (pageIdx > 0) pdf.addPage();
                    pdf.addImage(imgData, "JPEG", 0, 0, pdfWidth, pdfHeight);

                    position += chunkHeight;
                    heightLeft -= chunkHeight;
                    pageIdx++;
                }
            }

            // 4. Save PDF file on user device
            pdf.save(`mNetrics_Commercial_Agreement_${moment().format("YYYY-MM-DD")}.pdf`);
            Swal.close();

        } catch (err) {
            Swal.close();
            console.error("[Dashboard] Convert DOCX to PDF error:", err);
            Swal.fire({ icon: "error", text: err.message || "Failed to convert agreement to PDF.", ...SWAL_SETTINGS });
        } finally {
            if (tempContainer && document.body.contains(tempContainer)) {
                document.body.removeChild(tempContainer);
            }
        }
    };

    const handleDownloadPDF = handleDownloadAgreement;

    // UI Stepper Matching Reference Screenshot with Desktop & Mobile Responsiveness
    const renderStepper = () => {
        const steps = [
            { id: 1, label: "Commercial Agreement" },
            { id: 2, label: "Stripe Mandate Setup" },
            { id: 3, label: "Setup Complete" }
        ];

        const clubName = statusData?.clubDetails?.name || statusData?.clubName || (statusData?.firstName ? `${statusData.firstName} ${statusData.lastName || ''}`.trim() : "") || authName || "Club Admin";
        const clubImage = statusData?.clubDetails?.logo || statusData?.clubDetails?.image || statusData?.profile_image || null;
        const displayEmail = statusData?.email || "";
        const clubInitial = clubName ? clubName.charAt(0).toUpperCase() : "C";

        return (
            <div className="w-100 mb-3 mb-md-4 onboarding-header-outer">
                <div className="onboarding-header-inner">

                    {/* Brand/Title on Left (Desktop) / Top Left (Mobile) */}
                    <div className="onboarding-brand-box onboarding-mobile-top-bar">
                        <div className="d-flex align-items-center gap-2">
                            <span style={{ fontWeight: 800, fontSize: 18, color: "#1e293b", letterSpacing: "-0.5px" }}>
                                <span style={{ color: "#007d7f" }}>mNetrics</span> Billing
                            </span>
                        </div>

                        {/* Profile Avatar on Mobile (hidden on desktop) */}
                        <div className="d-block d-md-none position-relative" ref={popupRef}>
                            <button
                                type="button"
                                className="btn d-inline-flex align-items-center gap-1 p-1 border-0"
                                onClick={() => setShowLogoutPopup(!showLogoutPopup)}
                                style={{ background: "transparent", cursor: "pointer", boxShadow: "none" }}
                            >
                                {clubImage ? (
                                    <img
                                        src={clubImage}
                                        alt={clubName}
                                        style={{
                                            width: 34, height: 34, borderRadius: "50%",
                                            objectFit: "cover",
                                            border: "2px solid #007d7f"
                                        }}
                                    />
                                ) : (
                                    <div style={{
                                        width: 34, height: 34, borderRadius: "50%",
                                        background: "linear-gradient(135deg, #007d7f 0%, #5c56f6 100%)",
                                        color: "#ffffff", fontWeight: 700, fontSize: 14,
                                        display: "flex", alignItems: "center", justifyContent: "center"
                                    }}>
                                        {clubInitial}
                                    </div>
                                )}
                                <i className={`fa fa-chevron-${showLogoutPopup ? "up" : "down"}`} style={{ fontSize: 10, color: "#64748b" }}></i>
                            </button>

                            {/* Mobile Logout Dropdown Popup */}
                            {showLogoutPopup && (
                                <div className="position-absolute end-0 mt-2 p-3 text-start bg-white border"
                                    style={{
                                        width: 240,
                                        borderRadius: 12,
                                        boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                                        zIndex: 9999,
                                        borderColor: "#e2e8f0"
                                    }}
                                >
                                    <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                                        {clubImage ? (
                                            <img
                                                src={clubImage}
                                                alt={clubName}
                                                style={{
                                                    width: 34, height: 34, borderRadius: "50%",
                                                    objectFit: "cover",
                                                    border: "1.5px solid #007d7f"
                                                }}
                                            />
                                        ) : (
                                            <div style={{
                                                width: 34, height: 34, borderRadius: "50%",
                                                background: "linear-gradient(135deg, #007d7f 0%, #5c56f6 100%)",
                                                color: "#ffffff",
                                                fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center"
                                            }}>
                                                {clubInitial}
                                            </div>
                                        )}
                                        <div style={{ overflow: "hidden", flex: 1 }}>
                                            <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                                {clubName}
                                            </div>
                                            {displayEmail && (
                                                <div style={{ fontSize: 11, color: "#64748b", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                                    {displayEmail}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <button
                                        type="button"
                                        className="btn btn-danger w-100 d-flex align-items-center justify-content-center gap-2 mt-2"
                                        onClick={handleLogout}
                                        style={{
                                            fontWeight: 700,
                                            fontSize: 13,
                                            borderRadius: 8,
                                            padding: "8px 16px",
                                            backgroundColor: "#ef4444",
                                            borderColor: "#ef4444"
                                        }}
                                    >
                                        <i className="fa fa-sign-out-alt"></i> Logout
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stepper Steps in Center */}
                    <div className="onboarding-stepper-box">
                        {steps.map((step, index) => {
                            const isCompleted = currentStep > step.id || (currentStep === 3 && step.id === 3);
                            const isActive = currentStep === step.id && !isCompleted;
                            return (
                                <React.Fragment key={step.id}>
                                    <div className="onboarding-step-item">
                                        <div
                                            className="onboarding-step-circle"
                                            style={{
                                                borderRadius: "50%",
                                                display: "flex", alignItems: "center", justifyContent: "center",
                                                background: isCompleted ? "#2ed573" : isActive ? "#5c56f6" : "#f1f5f9",
                                                color: (isCompleted || isActive) ? "#ffffff" : "#94a3b8",
                                                fontWeight: 700,
                                                boxShadow: isActive ? "0 4px 12px rgba(92, 86, 246, 0.25)" : "none",
                                                transition: "all 0.3s ease",
                                                flexShrink: 0
                                            }}
                                        >
                                            {isCompleted ? <i className="fa fa-check"></i> : step.id}
                                        </div>
                                        <span className="onboarding-step-label" style={{
                                            fontWeight: isActive ? 700 : 600,
                                            color: isActive ? "#5c56f6" : isCompleted ? "#2ed573" : "#64748b"
                                        }}>
                                            {step.label}
                                        </span>
                                    </div>
                                    {index < steps.length - 1 && (
                                        <div className="onboarding-step-line" style={{
                                            background: currentStep > step.id ? "#2ed573" : "#e2e8f0",
                                            transition: "all 0.3s ease"
                                        }}></div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>

                    {/* Top Right: Desktop Profile Avatar & Logout Dropdown (hidden on mobile) */}
                    <div className="d-none d-md-block onboarding-profile-box position-relative" ref={popupRef}>
                        <button
                            type="button"
                            className="btn d-inline-flex align-items-center gap-2 p-1 border-0 ms-auto"
                            onClick={() => setShowLogoutPopup(!showLogoutPopup)}
                            style={{ background: "transparent", cursor: "pointer", boxShadow: "none" }}
                        >
                            {clubImage ? (
                                <img
                                    src={clubImage}
                                    alt={clubName}
                                    style={{
                                        width: 38, height: 38, borderRadius: "50%",
                                        objectFit: "cover",
                                        border: "2px solid #007d7f",
                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                                    }}
                                />
                            ) : (
                                <div style={{
                                    width: 38, height: 38, borderRadius: "50%",
                                    background: "linear-gradient(135deg, #007d7f 0%, #5c56f6 100%)",
                                    color: "#ffffff", fontWeight: 700, fontSize: 16,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                                }}>
                                    {clubInitial}
                                </div>
                            )}
                            <div className="d-flex flex-column text-start">
                                <span style={{ fontSize: 13, fontWeight: 700, color: "#1e293b", lineHeight: 1.2, maxWidth: 140, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                    {clubName}
                                </span>
                                {displayEmail && (
                                    <span style={{ fontSize: 11, color: "#64748b", lineHeight: 1.2, maxWidth: 140, textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                        {displayEmail}
                                    </span>
                                )}
                            </div>
                            <i className={`fa fa-chevron-${showLogoutPopup ? "up" : "down"} ms-1`} style={{ fontSize: 11, color: "#64748b" }}></i>
                        </button>

                        {/* Desktop Logout Dropdown Popup */}
                        {showLogoutPopup && (
                            <div className="position-absolute end-0 mt-2 p-3 text-start bg-white border"
                                style={{
                                    width: 250,
                                    borderRadius: 12,
                                    boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
                                    zIndex: 9999,
                                    borderColor: "#e2e8f0"
                                }}
                            >
                                <div className="d-flex align-items-center gap-2 mb-2 pb-2 border-bottom">
                                    {clubImage ? (
                                        <img
                                            src={clubImage}
                                            alt={clubName}
                                            style={{
                                                width: 38, height: 38, borderRadius: "50%",
                                                objectFit: "cover",
                                                border: "1.5px solid #007d7f"
                                            }}
                                        />
                                    ) : (
                                        <div style={{
                                            width: 38, height: 38, borderRadius: "50%",
                                            background: "linear-gradient(135deg, #007d7f 0%, #5c56f6 100%)",
                                            color: "#ffffff",
                                            fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center"
                                        }}>
                                            {clubInitial}
                                        </div>
                                    )}
                                    <div style={{ overflow: "hidden", flex: 1 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                            {clubName}
                                        </div>
                                        {displayEmail && (
                                            <div style={{ fontSize: 11, color: "#64748b", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                                                {displayEmail}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    className="btn btn-danger w-100 d-flex align-items-center justify-content-center gap-2 mt-2"
                                    onClick={handleLogout}
                                    style={{
                                        fontWeight: 700,
                                        fontSize: 13,
                                        borderRadius: 8,
                                        padding: "8px 16px",
                                        backgroundColor: "#ef4444",
                                        borderColor: "#ef4444"
                                    }}
                                >
                                    <i className="fa fa-sign-out-alt"></i> Logout
                                </button>
                            </div>
                        )}
                    </div>

                </div>
            </div>
        );
    };

    const isVerifyingOrLoading = isStatusLoading || verifySetupMutation.isLoading || (!!sessionIdInUrl && currentStep !== 3 && !verifySetupMutation.isError);

    return (
        <div style={{ backgroundColor: "#f8fafc", minHeight: "100vh", paddingBottom: "60px" }}>
            <style>{`
                .onboarding-header-outer {
                    background-color: #ffffff;
                    border-bottom: 1px solid #e2e8f0;
                    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.02);
                }
                .onboarding-header-inner {
                    max-width: 1200px;
                    margin: 0 auto;
                    padding: 14px 24px;
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    width: 100%;
                    box-sizing: border-box;
                }
                @media (min-width: 768px) {
                    .onboarding-header-inner {
                        flex-direction: row !important;
                    }
                    .onboarding-brand-box {
                        min-width: 180px !important;
                        flex-shrink: 0 !important;
                    }
                    .onboarding-stepper-box {
                        flex: 1 !important;
                        max-width: 650px !important;
                        margin: 0 32px !important;
                        display: flex !important;
                        align-items: flex-start !important;
                        justify-content: center !important;
                    }
                    .onboarding-profile-box {
                        min-width: 180px !important;
                        flex-shrink: 0 !important;
                        text-align: right !important;
                    }
                    .onboarding-step-item {
                        flex: 1 !important;
                        min-width: 120px !important;
                        display: flex !important;
                        flex-direction: column !important;
                        align-items: center !important;
                    }
                    .onboarding-step-circle {
                        width: 38px !important;
                        height: 38px !important;
                        font-size: 15px !important;
                    }
                    .onboarding-step-label {
                        font-size: 13px !important;
                        margin-top: 8px !important;
                        white-space: nowrap !important;
                        text-align: center !important;
                    }
                    .onboarding-step-line {
                        flex: 1 !important;
                        height: 2px !important;
                        margin-top: 18px !important;
                        margin-left: -10px !important;
                        margin-right: -10px !important;
                    }
                }
                @media (max-width: 767.98px) {
                    .onboarding-header-inner {
                        flex-direction: column !important;
                        padding: 12px 16px !important;
                    }
                    .onboarding-mobile-top-bar {
                        display: flex !important;
                        justify-content: space-between !important;
                        align-items: center !important;
                        width: 100% !important;
                    }
                    .onboarding-stepper-box {
                        width: 100% !important;
                        max-width: 100% !important;
                        margin-top: 12px !important;
                        padding-top: 10px !important;
                        border-top: 1px solid #f1f5f9 !important;
                        display: flex !important;
                        align-items: flex-start !important;
                        justify-content: space-between !important;
                    }
                    .onboarding-step-item {
                        flex: 1 !important;
                        min-width: 0 !important;
                        display: flex !important;
                        flex-direction: column !important;
                        align-items: center !important;
                    }
                    .onboarding-step-circle {
                        width: 34px !important;
                        height: 34px !important;
                        font-size: 13px !important;
                    }
                    .onboarding-step-label {
                        font-size: 11px !important;
                        margin-top: 5px !important;
                        white-space: normal !important;
                        word-break: normal !important;
                        text-align: center !important;
                        line-height: 1.2 !important;
                    }
                    .onboarding-step-line {
                        flex: 1 !important;
                        height: 2px !important;
                        margin-top: 16px !important;
                        margin-left: -5px !important;
                        margin-right: -5px !important;
                    }
                    .onboarding-card-wrapper {
                        padding-left: 12px !important;
                        padding-right: 12px !important;
                    }
                    .onboarding-card-header {
                        padding: 20px 16px !important;
                    }
                    .onboarding-card-body {
                        padding: 20px 16px !important;
                    }
                    .onboarding-card-title {
                        font-size: 20px !important;
                    }
                }
            `}</style>
            {renderStepper()}

            <div className="onboarding-card-wrapper">
                {isVerifyingOrLoading ? (
                    <div className="card custom-card mx-auto text-center onboarding-card-body" style={{ maxWidth: 700, borderRadius: 16, padding: "60px 40px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)", marginTop: 20 }}>
                        <div className="spinner-border mx-auto mb-3" role="status" style={{ width: "3rem", height: "3rem", color: "#5c56f6" }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h4 className="onboarding-card-title" style={{ fontWeight: 800, color: "#2f3542" }}>
                            {sessionIdInUrl ? "Verifying Stripe Mandate & Setup..." : "Loading Billing Details..."}
                        </h4>
                        <p className="text-muted" style={{ fontSize: 14 }}>
                            {sessionIdInUrl ? "Please wait a moment while we finalize your onboarding status." : "Preparing your onboarding billing dashboard..."}
                        </p>
                    </div>
                ) : (
                    <>
                        {/* STEP 1: Agreement Screen */}
                        {currentStep === 1 && (
                            <div className="mx-auto" style={{ maxWidth: 880, borderRadius: 16, overflow: "hidden", boxShadow: "0 20px 40px -15px rgba(0,0,0,0.07)", backgroundColor: "#ffffff" }}>
                                {/* Header */}
                                <div className="onboarding-card-header" style={{ background: "#1b1e2e", color: "#ffffff", padding: "34px 40px 28px 40px" }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <span style={{ background: "#5c56f6", color: "#ffffff", padding: "5px 11px", borderRadius: 6, fontSize: 11, fontWeight: 800, letterSpacing: "0.5px" }}>
                                            STEP 1 OF 3
                                        </span>
                                        <i className="far fa-file-alt" style={{ fontSize: 24, color: "#94a3b8" }}></i>
                                    </div>
                                    <h3 className="onboarding-card-title" style={{ margin: "16px 0 8px", fontWeight: 700, fontSize: 26, color: "#ffffff" }}>
                                        {cmsAgreement.title || "Customer Agreement"}
                                    </h3>
                                    <p style={{ margin: 0, color: "#94a3b8", fontSize: 14 }}>
                                        Please review the terms, scroll to the bottom, and provide your digital signature to proceed.
                                    </p>
                                </div>

                                <div className="card-body onboarding-card-body" style={{ padding: "32px 40px 36px 40px" }}>
                                    <div>
                                        {cmsAgreement.loading ? (
                                            <div className="text-center py-5">
                                                <div className="spinner-border" style={{ color: "#5c56f6" }}></div>
                                                <p className="mt-2 text-muted">Loading Customer Agreement...</p>
                                            </div>
                                        ) : (
                                            <div>
                                                {/* 1:1 Word (.docx) Master Template Render Container */}
                                                <div className="mb-4">
                                                    <NativeDocxViewer />
                                                </div>

                                                {/* Integrated Execution Block Digital Signature (Black Pen) */}
                                                <div className="p-3 p-sm-4 mt-3" style={{ background: "#ffffff", border: "2px solid #007d7f", borderRadius: 12, boxShadow: "0 6px 20px rgba(0,125,127,0.08)" }}>
                                                    <div className="mb-3">
                                                        <p className="text-muted tx-12 mb-0">
                                                            Draw your digital signature below using your mouse or touch pen to sign directly in the execution block.
                                                        </p>
                                                    </div>
                                                    <div className="row g-2 mb-3">
                                                        <div className="col-12 col-md-6 mb-2 mb-md-0">
                                                            <label style={{ fontSize: 11, fontWeight: 700, color: "#007d7f", letterSpacing: "0.5px" }}> AUTHORISED REPRESENTATIVE NAME <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="Representative name"
                                                                value={authName}
                                                                onChange={(e) => setAuthName(e.target.value)}
                                                                style={{ borderRadius: 6, borderColor: "#cbd5e1", fontSize: 13, padding: "8px 12px" }}
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <label style={{ fontSize: 11, fontWeight: 700, color: "#007d7f", letterSpacing: "0.5px" }}>OFFICIAL TITLE <span className="text-danger">*</span></label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="e.g. Director / Club President"
                                                                value={authTitle}
                                                                onChange={(e) => setAuthTitle(e.target.value)}
                                                                style={{ borderRadius: 6, borderColor: "#cbd5e1", fontSize: 13, padding: "8px 12px" }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="row g-2 mb-3">
                                                        <div className="col-12 col-md-6 mb-2 mb-md-0">
                                                            <label style={{ fontSize: 11, fontWeight: 700, color: "#007d7f", letterSpacing: "0.5px" }}>FULL COMPANY NAME</label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="e.g. Full Registered Company / Club Name"
                                                                value={fullCompanyName}
                                                                onChange={(e) => setFullCompanyName(e.target.value)}
                                                                style={{ borderRadius: 6, borderColor: "#cbd5e1", fontSize: 13, padding: "8px 12px" }}
                                                            />
                                                        </div>
                                                        <div className="col-12 col-md-6">
                                                            <label style={{ fontSize: 11, fontWeight: 700, color: "#007d7f", letterSpacing: "0.5px" }}>COMPANY NUMBER</label>
                                                            <input
                                                                type="text"
                                                                className="form-control form-control-sm"
                                                                placeholder="e.g. 12345678 or N/A"
                                                                value={companyNumber}
                                                                onChange={(e) => setCompanyNumber(e.target.value)}
                                                                style={{ borderRadius: 6, borderColor: "#cbd5e1", fontSize: 13, padding: "8px 12px" }}
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="mb-3">
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <label style={{ fontSize: 11, fontWeight: 700, color: signatureError ? "#dc3545" : "#007d7f", letterSpacing: "0.5px", margin: 0 }}>
                                                                DRAW DIGITAL SIGNATURE <span className="text-danger">*</span>
                                                            </label>
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary"
                                                                onClick={handleClearSignature}
                                                                style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px" }}
                                                            >
                                                                <i className="fa fa-eraser me-1"></i> Clear
                                                            </button>
                                                        </div>
                                                        <CanvasSignaturePad
                                                            onCanvasRef={handleCanvasRef}
                                                            setHasSignature={setHasSignature}
                                                            hasError={!!signatureError}
                                                            onStroke={() => {
                                                                if (signatureError) setSignatureError("");
                                                            }}
                                                        />
                                                        {signatureError && (
                                                            <div className="text-danger mt-1" style={{ fontSize: 12, fontWeight: 600 }}>
                                                                <i className="fa fa-exclamation-circle me-1"></i> {signatureError}
                                                            </div>
                                                        )}
                                                    </div>

                                                    <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3 pt-2">
                                                        <div className="d-flex align-items-start gap-2 w-100 w-sm-auto" style={{ maxWidth: 500 }}>
                                                            <input
                                                                type="checkbox"
                                                                id="ratifyCheckbox"
                                                                checked={isRatified}
                                                                onChange={(e) => setIsRatified(e.target.checked)}
                                                                style={{
                                                                    accentColor: "#007d7f",
                                                                    width: 18,
                                                                    height: 18,
                                                                    cursor: "pointer",
                                                                    marginTop: 2,
                                                                    flexShrink: 0
                                                                }}
                                                            />
                                                            <label htmlFor="ratifyCheckbox" style={{ fontSize: 12, color: "#475569", lineHeight: 1.4, cursor: "pointer", margin: 0 }}>
                                                                By ticking this box, I agree to the terms of this agreement and confirm that I am authorised to enter into it on behalf of my organisation.
                                                            </label>
                                                        </div>

                                                        <button
                                                            className="btn w-100 w-sm-auto"
                                                            onClick={handleSignNormalAgreement}
                                                            disabled={signNormalAgreementMutation.isLoading}
                                                            style={{
                                                                background: "#007d7f",
                                                                color: "#ffffff",
                                                                fontWeight: 700,
                                                                borderRadius: 8,
                                                                padding: "12px 32px",
                                                                fontSize: 14,
                                                                border: "none",
                                                                boxShadow: "0 4px 14px rgba(0, 125, 127, 0.3)",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                gap: 8,
                                                                whiteSpace: "nowrap"
                                                            }}
                                                        >
                                                            {signNormalAgreementMutation.isLoading ? (
                                                                <span><span className="spinner-border spinner-border-sm me-2"></span>Executing Agreement...</span>
                                                            ) : (
                                                                <span>Agree & Continue <i className="fa fa-arrow-right ms-1"></i></span>
                                                            )}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>         
                                </div>
                            </div>
                        )}

                        {/* STEP 2: Stripe Mandate Setup */}
                        {currentStep === 2 && clientSecret && (
                            <div className="card custom-card mx-auto" style={{ maxWidth: 800, borderRadius: 16, overflow: "hidden", boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
                                <div className="onboarding-card-header" style={{ background: "#6c5ce7", color: "#fff", padding: "30px 40px" }}>
                                    <div className="d-flex justify-content-between align-items-center">
                                        <div>
                                            <span style={{ background: "rgba(255,255,255,0.2)", color: "#fff", padding: "4px 12px", borderRadius: 4, fontSize: 11, fontWeight: 800, letterSpacing: 1 }}>STEP 2 OF 3</span>
                                            <h3 className="onboarding-card-title" style={{ margin: "16px 0 8px", fontWeight: 700, fontSize: 24, display: "flex", alignItems: "center", gap: 10 }}>
                                                <i className="fab fa-stripe" style={{ fontSize: 32 }}></i> Stripe Payment Mandate Setup
                                            </h3>
                                            <p style={{ margin: 0, color: "rgba(255,255,255,0.85)", fontSize: 14 }}>
                                                Commercial Agreement signed. Now complete your Stripe BACS Direct Debit payment mandate.
                                            </p>
                                        </div>
                                        <i className="fa fa-university fa-3x d-none d-sm-block" style={{ color: "rgba(255,255,255,0.8)" }}></i>
                                    </div>
                                </div>

                                <div className="card-body onboarding-card-body" style={{ padding: "20px 40px 40px" }}>
                                    <EmbeddedCheckoutProvider stripe={stripePromise} options={{ clientSecret }}>
                                        <EmbeddedCheckout />
                                    </EmbeddedCheckoutProvider>
                                </div>
                            </div>
                        )}

                        {/* STEP 3: Onboarding Complete & Dashboard */}
                        {currentStep === 3 && (
                            <div className="card custom-card mx-auto text-center onboarding-card-body" style={{ maxWidth: 700, borderRadius: 16, padding: "60px 40px", boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>
                                <i className="fa fa-check-circle fa-5x mb-4" style={{ color: "#2ed573" }}></i>
                                <h2 className="onboarding-card-title" style={{ fontWeight: 800, color: "#2f3542", marginBottom: 12 }}>
                                    {t("onboarding_billing_complete", "Onboarding Billing Complete!")}
                                </h2>
                                <p style={{ color: "#57606f", fontSize: 15, marginBottom: 24 }}>
                                    {t("onboarding_billing_complete_desc", {
                                        defaultValue: `Your Commercial Agreement has been signed and your Stripe Direct Debit Mandate is active.`
                                    })}
                                </p>

                                <div className="d-flex flex-column flex-sm-row justify-content-center gap-2 gap-sm-3 w-100">
                                    <button
                                        type="button"
                                        className="btn w-100 w-sm-auto"
                                        style={{ background: "#5c56f6", color: "#ffffff", fontWeight: 700, padding: "12px 24px", borderRadius: 8, border: "1px solid #5c56f6", boxShadow: "0 4px 12px rgba(92, 86, 246, 0.2)" }}
                                        onClick={() => portalSessionMutation.mutate()}
                                        disabled={portalSessionMutation.isLoading}
                                    >
                                        <i className="fa fa-external-link-alt me-2"></i> {portalSessionMutation.isLoading ? "Loading..." : "Go To Customer Portal"}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn w-100 w-sm-auto"
                                        style={{ background: "#ffffff", border: "1px solid #dfe4ea", color: "#2f3542", fontWeight: 700, padding: "12px 24px", borderRadius: 8, boxShadow: "0 2px 6px rgba(0, 0, 0, 0.05)" }}
                                        onClick={handleDownloadAgreement}
                                    >
                                        <i className="fa fa-file-pdf text-primary me-2"></i>Download Agreement
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default ClubBillingDashboard;
